import type { AgendaActivity, AvailabilityItem, UnavailableReason } from '@koderek/shared';
import { Badge, Button, Group, Modal, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { t } from '../../texts/pl';
import { publicStateText, reasonText } from './reasons';
import type { EnrollmentControls } from './useEnrollment';

/** Powody, które tylko informują (nie są blokadą dla tej osoby) – bez czerwonego. */
const INFO_REASONS = new Set<UnavailableReason>(['not_open_yet', 'no_enrollment', 'cancelled']);

interface Props {
  activity: AgendaActivity;
  controls: EnrollmentControls | null;
  live: AvailabilityItem | undefined;
  opensAt: string | undefined;
  compact?: boolean;
}

/**
 * US-501…US-503: akcja dla wybranej osoby – „Zapisz”, „Zapisany” + „Wypisz” (zawsze z pytaniem, Z-501) albo powód
 * niedostępności z serwera (US-502). Bez wybranej osoby (gość) – tylko stan zajęć (US-502 AC-2).
 */
export function EnrollmentAction({ activity, controls, live, opensAt, compact = false }: Props) {
  const [confirm, setConfirm] = useState(false);
  const size = compact ? 'compact-xs' : 'sm';
  const item = controls?.items.get(activity.id);

  if (!controls || !item) {
    const text = live ? publicStateText(live.state, opensAt) : null;
    return text && live?.state !== 'no_enrollment' && live?.state !== 'cancelled' ? (
      <Text size="xs" c="dimmed">
        {text}
      </Text>
    ) : null;
  }

  const pending = controls.pendingActivityId === activity.id;
  if (item.state === 'enrolled') {
    return (
      <>
        <Group gap={4} wrap="wrap">
          <Badge color="teal" size={compact ? 'xs' : 'sm'}>
            {t.enrollment.enrolled}
          </Badge>
          {item.byStaff ? (
            <Text size="xs" c="dimmed">
              {t.enrollment.byStaff}
            </Text>
          ) : null}
          {item.canUnenroll ? (
            <Button size={size} variant="subtle" color="red" loading={pending} onClick={(e) => { e.stopPropagation(); setConfirm(true); }}>
              {t.enrollment.unenroll}
            </Button>
          ) : (
            <Text size="xs" c="dimmed">
              {t.enrollment.unenrollStarted}
            </Text>
          )}
        </Group>
        <Modal opened={confirm} onClose={() => setConfirm(false)} title={t.enrollment.unenrollTitle}>
          <Stack gap="sm">
            <Text>{t.enrollment.unenrollQuestion(controls.personName, `${activity.symbol} ${activity.title}`)}</Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setConfirm(false)}>
                {t.registration.cancel}
              </Button>
              <Button
                color="red"
                onClick={() => {
                  setConfirm(false);
                  controls.unenroll(activity);
                }}
              >
                {t.enrollment.unenrollConfirm}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </>
    );
  }
  if (item.state === 'available') {
    return (
      <Button size={size} loading={pending} onClick={(e) => { e.stopPropagation(); controls.enroll(activity); }} w="fit-content">
        {t.enrollment.enroll}
      </Button>
    );
  }
  // Wstęp wolny i odwołanie widać już na kafelku – nie powtarzamy.
  if (compact && (item.reason === 'no_enrollment' || item.reason === 'cancelled')) return null;
  // Powód blokady dla tej osoby (wiek, kolizja, brak miejsc…) – na czerwono; informacje („Zapisy od…”, wstęp wolny) – szare.
  const blocking = !!item.reason && !INFO_REASONS.has(item.reason);
  return (
    <Text size="xs" c={blocking ? 'red.7' : 'dimmed'} fw={blocking ? 500 : undefined} data-reason>

      {item.reason ? reasonText(item.reason, item.data) : null}
    </Text>
  );
}
